// src/theme/index.js
import {
  createTheme as createMuiTheme,
  responsiveFontSizes,
} from "@mui/material/styles";
import _ from "lodash";

// Define your typography styles with unitless line heights
const typography = {
  h1: {
    fontWeight: 700,
    fontSize: "32px",
    fontFamily: "'Inter', sans-serif",
    lineHeight: 1.5,
    "@media(max-width:767px)": {
      fontSize: "25px",
      lineHeight: 1.6,
      fontWeight: 600,
    },
  },
  h2: {
    fontWeight: 700,
    fontSize: "24px",
    fontFamily: "'Inter', sans-serif",
    lineHeight: 1.2,
    "@media(max-width:767px)": {
      fontSize: "20px",
      lineHeight: 1.3,
      fontWeight: 600,
    },
  },
  h3: {
    fontWeight: 500,
    fontSize: "20px",
    fontFamily: "'Inter', sans-serif",
    lineHeight: 1.2,
    "@media(max-width:767px)": {
      fontSize: "20px",
      lineHeight: 1.3,
    },
  },
  h4: {
    fontWeight: 500,
    fontSize: "20px",
    fontFamily: "'Inter', sans-serif",
    lineHeight: 1.2,
    "@media(max-width:767px)": {
      fontSize: "18px",
      lineHeight: 1.3,
    },
  },
  h5: {
    fontWeight: 500,
    fontSize: "18px",
    fontFamily: "'Inter', sans-serif",
    lineHeight: 1.67,
    "@media(max-width:767px)": {
      fontSize: "16px",
      lineHeight: 1.3,
    },
  },
  h6: {
    fontWeight: 300,
    fontSize: "16px",
    fontFamily: "'Inter', sans-serif",
    lineHeight: 1.75,
    "@media(max-width:767px)": {
      fontSize: "14px",
      lineHeight: 1.3,
    },
  },
  overline: {
    fontWeight: 500,
    fontFamily: "'Inter', sans-serif",
  },
  button: {
    textTransform: "capitalize",
    borderRadius: 27,
    fontFamily: "'Inter', sans-serif",
  },
  body1: {
    fontSize: "16px",
    fontWeight: 400,
    fontFamily: "'Inter', sans-serif",
    lineHeight: "17px",
    "@media(max-width:767px)": {
      fontSize: "18px",
      lineHeight: 1.67,
    },
  },
  body2: {
    fontSize: "14px",
    fontWeight: 400,
    fontFamily: "'Inter', sans-serif",
    lineHeight: "20px",
    "@media(max-width:767px)": {
      fontSize: "14px",
    },
  },
  subtitle1: {
    fontSize: "11px",
    fontFamily: "'Inter', sans-serif",
    fontWeight: 300,
    lineHeight: 1.2,
  },
};

const baseOptions = {
  direction: "ltr",
  typography,
  components: {
    // Define your component overrides here
  },
};

const themesOptions = [
  {
    name: "LIGHT",
    palette: {
      mode: "light",
      primary: {
        main: "#0B1426",
      },
      secondary: {
        main: "#0B1426",
      },
      background: {
        default: "#fff",
        paper: "#FFFFFF",
      },
      text: {
        primary: "#000000",
        secondary: "#000000",
      },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: "#fff",
            boxShadow: "none",
            border: "1px solid #E5E5E5",
            borderRadius: "0",
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            color: "#000",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          fullWidth: {
            borderRadius: "8px",
          },
          "& .MuiOutlinedInput-inputMarginDense": {
            padding: "8px 13px",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: { borderColor: "rgba(216, 216, 216, 1)" },
          root: {
            background: "#fff",
            fontSize: "16px",
            color: "black",
            borderRadius: "8px",
            border: "none",
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "none",
            },
            "&::placeholder": {
              color: "#7C7C7C !important",
              fontFamily: "'Inter', sans-serif",
              fontStyle: "normal",
              fontWeight: "400",
              fontSize: "16px",
              lineHeight: "125%",
            },
            input: {
              color: "#163041 !important",
              fontFamily: "'Inter', sans-serif",
              fontSize: "16px",
              lineHeight: "125%",
              fontWeight: "400",
              padding: "10.5px 16px !important",
            },
            adornedStart: {
              paddingLeft: "0px",
            },
            adornedEnd: {
              paddingRight: "0px",
            },
            "& .MuiInputBase-input": {
              borderColor: "none",
            },
            "& .MuiOutlinedInput-inputMarginDense": {
              padding: "18px 16px !important",
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "none",
              },
              "&:hover fieldset": {
                borderColor: "none",
              },
              "&.Mui-focused fieldset": {
                borderColor: "none",
              },
            },
          },
        },

      },
      MuiSelect: {
        styleOverrides: {
          select: {
            padding: "10px 13px",
            textAlign: "start",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: "rgba(255, 255, 255, 1)",
            borderRadius: "0px",
            boxShadow:
              "0px 2px 1px -1px rgba(0,0,0,0.06),0px 1px 1px 0px rgba(0,0,0,0.064),0px 1px 3px 0px rgba(0,0,0,0.06)",
          },
          elevation2: {
            backgroundColor: "rgba(255, 255, 255, 1)",
            borderRadius: "14px",
            boxShadow:
              "0px 2px 1px -1px rgba(0,0,0,0.06),0px 1px 1px 0px rgba(0,0,0,0.064),0px 1px 3px 0px rgba(0,0,0,0.06)",
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            borderRadius: "10px",
            border: "1px solid #E8E7E7",
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            background: "transparent",
            borderTop: "1px solid #636262",
            "&:hover": {
              backgroundColor: "none",
            },
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            background: "#fff",
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            borderBottom: "1px solid #E8E7E7",
            "&:hover": {
              backgroundColor: "#ffffff14",
            },
            "&:last-child": {
              borderBottom: "none",
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            padding: "4px 8px",
            fontWeight: "500",
            backgroundColor: "#0e539e",
            color: "#fff",
            // whiteSpace: "pre",
            fontSize: "14px",
            textAlign: "center ",
            minWidth: "75px"
          },
          root: {
            padding: "4px 8px",
          },
          body: {
            color: "#0B1426",
            // whiteSpace: "pre",
            fontSize: "14px",
            fontWeight: "500",
            textAlign: "center",
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          list: {
            outline: "0",
            background: "#ffffff",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            color: "#fff",
            padding: "8px 20px",
            fontWeight: "500",
            fontSize: "14px",
            lineHeight: "18px",
            borderRadius: "8px",
            backgroundColor: "#0b529d",
            height:"34px",
            "&:hover": {
              color: "#fff",
              backgroundColor: "#0b529d",
            },
            "@media(max-width:767px)": {
              padding: "7px 15px",
            },
          },
          containedSecondary: {
            backgroundColor: "#0b529d",
            padding: "8px 27px",
            filter: "drop-shadow(0px 13px 27px rgba(0, 0, 0, 0.25))",
            fontWeight: "500",
            fontSize: "14px",
            lineHeight: "18px",
            color: "#fff",
            borderRadius: "8px",
            border: "2px solid ",
            borderColor: "#0b529d",
            "&:hover": {
              color: "#fff",
              background: "#0b529d",
              borderColor: "#0b529d",
             backgroundColor: "#0b529d",
            },
            "@media(max-width:767px)": {
              padding: "7px 15px",
            },
          },
          contained: {
            background: "#0b529d",
            fontWeight: "500",
            fontSize: "14px",
            lineHeight: "18px",
            "&.Mui-disabled": {
              backgroundColor: "rgba(0, 0, 0, 0.03) ",
            },
            "@media(max-width:767px)": {
              padding: "10px 15px",
            },
          },
          outlinedPrimary: {
            color: "#fff",
            backgroundColor: "#0b529d",
            fontWeight: "500",
            fontSize: "14px",
            lineHeight: "18px",
            borderRadius: "8px",
            border: "2px solid #0b529d !important",
            "&:hover": {
              color: "#fff",
              boxShadow: "none !important",
              backgroundColor: "#0b529d",
            },
            "@media(max-width:767px)": {
              padding: "7px 15px",
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: "#000000",
            "& svg": {
              color: "#000000",
            },
          },
          MuiSvgIcon: {
            styleOverrides: {
              root: {
                color: "#000",
              },
            },
          },
        },
      },
    },
  },
  {
    name: "DARK",
    palette: {
      mode: "light",
      primary: {
        main: "#0B1426",
      },
      secondary: {
        main: "#0B1426",
      },
      background: {
        default: "#fff",
        paper: "#FFFFFF",
      },
      text: {
        primary: "#000000",
        secondary: "#000000",
      },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: "#fff",
            boxShadow: "none",
            border: "1px solid #E5E5E5",
            borderRadius: "0",
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            color: "#000",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          fullWidth: {
            borderRadius: "8px",
          },
          "& .MuiOutlinedInput-inputMarginDense": {
            padding: "8px 13px",
          },
        },
      },
      styleOverrides: {
        notchedOutline: { borderColor: "rgba(216, 216, 216, 1)" },
        root: {
          background: "#fff",
          fontSize: "16px",
          color: "black",
          borderRadius: "8x",
          border: "none",
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "none",
          },
          "&::placeholder": {
            color: "rgba(0, 0, 0, 0.50) !important",
            fontFamily: "'Inter', sans-serif",
            fontStyle: "normal",
            fontWeight: "400",
            fontSize: "16px",
            lineHeight: "125%",
          },
          input: {
            color: "black !important",
            fontFamily: "'Inter', sans-serif",
            fontSize: "16px",
            lineHeight: "125%",
            fontWeight: "400",
            padding: "18px 16px !important",
          },
          adornedStart: {
            paddingLeft: "0px",
          },
          adornedEnd: {
            paddingRight: "0px",
          },
          "& .MuiInputBase-input": {
            borderColor: "none",
          },
          "& .MuiOutlinedInput-inputMarginDense": {
            // padding: "18px 16px !important",
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "none",
            },
            "&:hover fieldset": {
              borderColor: "none",
            },
            "&.Mui-focused fieldset": {
              borderColor: "none",
            },
          },
        },

      },
      MuiSelect: {
        styleOverrides: {
          select: {
            padding: "10px 13px",
            textAlign: "start",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: "rgba(255, 255, 255, 1)",
            borderRadius: "0px",
            boxShadow:
              "0px 2px 1px -1px rgba(0,0,0,0.06),0px 1px 1px 0px rgba(0,0,0,0.064),0px 1px 3px 0px rgba(0,0,0,0.06)",
          },
          elevation2: {
            backgroundColor: "rgba(255, 255, 255, 1)",
            borderRadius: "14px",
            boxShadow:
              "0px 2px 1px -1px rgba(0,0,0,0.06),0px 1px 1px 0px rgba(0,0,0,0.064),0px 1px 3px 0px rgba(0,0,0,0.06)",
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            borderRadius: "10px",
            border: "1px solid #E8E7E7",
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            background: "transparent",
            borderTop: "1px solid #636262",
            "&:hover": {
              backgroundColor: "none",
            },
          },
        },
      },
      MuiTableBody: {
        styleOverrides: {
          root: {
            background: "#fff",
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            borderBottom: "1px solid #E8E7E7",
            "&:hover": {
              backgroundColor: "#ffffff14",
            },
            "&:last-child": {
              borderBottom: "none",
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            padding: "4px 8px",
            fontWeight: "500",
            backgroundColor: "#0e539e",
            color: "#fff",
            // whiteSpace: "pre",
            fontSize: "14px",
            textAlign: "center ",
          },
          root: {
            padding: "4px 8px",
            minWidth:"75px"
          },
          body: {
            color: "#0B1426",
            // whiteSpace: "pre",
            fontSize: "14px",
            fontWeight: "500",
            textAlign: "center",
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          list: {
            outline: "0",
            background: "#ffffff",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            color: "#fff",
            padding: "8px 20px",
            fontWeight: "500",
            fontSize: "14px",
            lineHeight: "18px",
            borderRadius: "8px",
            backgroundColor: "#0b529d",
            height: "34px",
            "&:hover": {
              color: "#fff",
              backgroundColor: "#0b529d",
            },
            "@media(max-width:767px)": {
              padding: "7px 15px",
            },
          },
          containedSecondary: {
            backgroundColor: "#0b529d",
            padding: "8px 20px",
            filter: "drop-shadow(0px 13px 27px rgba(0, 0, 0, 0.25))",
            fontWeight: "500",
            fontSize: "14px",
            lineHeight: "18px",
            color: "#fff",
            borderRadius: "8px",
            border: "2px solid ",
            borderColor: "#0b529d",
            height: "34px",
            "&:hover": {
              color: "#fff",
              background: "#0b529d",
              borderColor: "#0b529d",
              backgroundColor: "#0b529d",
            },
            "@media(max-width:767px)": {
              padding: "7px 15px",
            },
          },
          contained: {
            background: "#0b529d",
            fontWeight: "500",
            fontSize: "14px",
            lineHeight: "18px",
            "&.Mui-disabled": {
              backgroundColor: "rgba(0, 0, 0, 0.03) ",
            },
            "@media(max-width:767px)": {
              padding: "7px 15px",
            },
          },
          outlinedPrimary: {
            color: "#fff",
            backgroundColor: "#0b529d",
            fontWeight: "500",
            fontSize: "14px",
            lineHeight: "18px",
            borderRadius: "8px",
            border: "2px solid #0b529d !important",
            "&:hover": {
              color: "#fff",
              boxShadow: "none !important",
              backgroundColor: "#0b529d",
            },
            "@media(max-width:767px)": {
              padding: "7px 15px",
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: "#000000",
            "& svg": {
              color: "#000000",
            },
          },
          MuiSvgIcon: {
            styleOverrides: {
              root: {
                color: "#000",
              },
            },
          },
        },
      },
    },
  },
];

export const createTheme = (config = {}) => {
  let themeOptions = themesOptions.find((theme) => theme.name === config.theme);

  if (!themeOptions) {
    console.warn(new Error(`The theme ${config.theme} is not valid`));
    [themeOptions] = themesOptions;
  }

  let theme = createMuiTheme(
    _.merge({}, baseOptions, themeOptions, { direction: config.direction })
  );

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};
